import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { COLLECTION_META, PRODUCTS } from "../data/catalog";
import { useEtsyListings, withEtsy } from "../data/etsy";
import { ETSY_SHOP } from "../data/site";
import { ProductCard } from "./ProductCard";
import { Reveal } from "./motion";

export default function ShopPage() {
  const [filter, setFilter] = useState("all");
  const etsyListings = useEtsyListings();
  const items = (filter === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.collection === filter)).map((p) =>
    withEtsy(p, etsyListings),
  );

  return (
    <main data-testid="shop-page">
      <div className="border-b border-navy/10 bg-blush">
        <div className="mx-auto max-w-[1600px] px-6 pt-36 pb-16 md:px-12 md:pt-44 md:pb-20">
          <Reveal>
            <Link
              to="/"
              data-testid="shop-back-link"
              className="group inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] text-navy/60 transition-colors duration-300 hover:text-navy"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              Home
            </Link>
            <h1 className="mt-10 max-w-5xl font-playfair text-5xl leading-[0.92] tracking-tight text-navy md:text-7xl lg:text-8xl">
              Browse all pieces.
            </h1>
            <p className="mt-8 max-w-xl text-sm leading-relaxed text-navy/60 md:text-base">
              The whole Dream Freely catalog in one place. Every piece links to its Etsy listing — anything marked
              <span className="font-medium text-navy"> Coming Soon</span> takes you to the shop so you can follow along
              for the drop.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={() => setFilter("all")}
                data-testid="shop-filter-all"
                className={`rounded-full border px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] transition-all duration-300 ${
                  filter === "all" ? "border-navy bg-navy text-cream" : "border-navy/30 text-navy/70 hover:border-navy hover:text-navy"
                }`}
              >
                All
              </button>
              {COLLECTION_META.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setFilter(c.name)}
                  data-testid={`shop-filter-${c.slug}`}
                  className={`rounded-full border px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] transition-all duration-300 ${
                    filter === c.name
                      ? "border-navy bg-navy text-cream"
                      : "border-navy/30 text-navy/70 hover:border-navy hover:text-navy"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-24">
        <Reveal>
          <p data-testid="shop-result-count" className="text-[11px] font-medium uppercase tracking-[0.3em] text-navy/50">
            {items.length} {filter === "all" ? "pieces" : `pieces in ${filter}`}
          </p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, i) => (
            <Reveal key={`${item.name}-${i}`} delay={Math.min((i % 4) * 0.05, 0.2)} className="h-full">
              <ProductCard item={item} setId="shop" />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-20 flex justify-center">
          <a
            href={ETSY_SHOP}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-follow-etsy-btn"
            className="group flex items-center gap-3 rounded-full bg-navy px-9 py-4 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-transform duration-300 hover:scale-95"
          >
            Follow the shop on Etsy
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </Reveal>
      </div>
    </main>
  );
}
